import { useEffect, useRef, useState } from 'react';

export type DamageLinesReload = () => void | Promise<unknown>;

export const useRefreshDamageLines = <T>() => {
  const [snapshot, setSnapshot] = useState<T[] | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refreshDamageLines = async (
    rows: T[],
    reload: DamageLinesReload,
    resetRecords: () => void,
  ) => {
    setSnapshot(rows);
    setRefreshing(true);
    try {
      await reload();
      if (mountedRef.current) resetRecords();
    } finally {
      if (mountedRef.current) {
        setSnapshot(null);
        setRefreshing(false);
      }
    }
  };

  return { snapshot, refreshing, refreshDamageLines };
};

export const mergeSavedRow = <T extends { lineCost?: number }>(
  rows: T[],
  saved: T,
  id: (row: T) => number | undefined,
): T[] => {
  const savedId = id(saved);
  if (!savedId) return rows;

  return rows.map((row) => {
    if (id(row) !== savedId) return row;
    const price = Number((saved as { price?: number }).price ?? 0);
    const quantity = Number((saved as { quantity?: number }).quantity ?? 0);
    return {
      ...row,
      ...saved,
      lineCost: price * quantity,
    };
  });
};
